/**
 * Bannière « Mode démo ». Affichée en haut de l'app quand le mode démo est
 * actif. Rappelle que les données sont fictives + lecture seule, permet de
 * relancer la visite guidée et de quitter la démo (retour à l'espace réel).
 *
 * Embarque aussi le pont qui transforme l'évènement « écriture bloquée »
 * (levé par `demoFetch`) en toast.
 */
import { useEffect, useRef } from "react";
import { DEMO_READONLY_EVENT, stopDemo } from "../../lib/demo/demoMode";
import { useToast } from "../../ui/Toast";
import { useAuth } from "../../auth/AuthContext";
import { startProductTour } from "./productTour";

export function DemoBanner() {
  const toast = useToast();
  const { contractSignatureRequired, mandateRequired } = useAuth();
  const lastShown = useRef(0);

  // Obligation réelle d'onboarding en attente derrière l'exploration démo.
  const pendingStep = contractSignatureRequired
    ? "signer votre contrat"
    : mandateRequired
      ? "mettre en place votre prélèvement SEPA"
      : null;

  useEffect(() => {
    const handler = () => {
      const now = performance.now();
      if (now - lastShown.current < 2500) return; // throttle
      lastShown.current = now;
      toast.info(
        "Mode démo : les modifications sont désactivées. Quittez la démo pour utiliser votre espace.",
      );
    };
    window.addEventListener(DEMO_READONLY_EVENT, handler);
    return () => window.removeEventListener(DEMO_READONLY_EVENT, handler);
  }, [toast]);

  function exit() {
    stopDemo();
    // Rechargement complet : vide le cache des requêtes (fixtures) et
    // recharge les vraies données du tenant. Les gates d'onboarding
    // réelles (signature/mandat) s'appliquent de nouveau automatiquement.
    window.location.assign("/");
  }

  return (
    <div className="relative z-[55]">
      <div className="flex flex-wrap items-center justify-center gap-x-4 gap-y-1 bg-gradient-to-r from-accent-600 to-accent-500 px-4 py-2 text-center text-sm text-white">
        <p className="font-semibold">
          <span aria-hidden="true">✨ </span>
          Mode démo — vous explorez une agence fictive (lecture seule)
        </p>
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={() => startProductTour()}
            className="rounded-full bg-white/15 px-3 py-1 text-xs font-semibold text-white transition hover:bg-white/25"
          >
            Relancer la visite
          </button>
          <button
            type="button"
            onClick={exit}
            className="rounded-full bg-white px-3 py-1 text-xs font-bold text-accent-700 transition hover:bg-white/90"
          >
            Quitter la démo
          </button>
        </div>
      </div>

      {pendingStep ? (
        <div className="flex flex-wrap items-center justify-center gap-x-3 gap-y-1 bg-amber-100 px-4 py-1.5 text-center text-xs text-amber-900">
          <p className="font-semibold">
            <span aria-hidden="true">⚠️ </span>
            Une étape reste à finaliser pour activer votre espace : {pendingStep}.
          </p>
          <button
            type="button"
            onClick={exit}
            className="rounded-full bg-amber-500 px-3 py-1 text-xs font-bold text-white transition hover:bg-amber-600"
          >
            Finaliser maintenant
          </button>
        </div>
      ) : null}
    </div>
  );
}
